import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  Modal,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import { IAddProject, InfoItem } from "@types";
import React from "react";
import CommonInput from "../../components/new/CommonComponents";
import { FilterIcon, Folder, InfoIcon, Upload } from "lucide-react";
import { extractImgPaths } from "../../utils/datasetFileUtil";
import { checkProjectName, createProject } from "../../services/projectService";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import { toast } from "react-toastify";
import ServerFileModal from "../../components/ServerFileModal";
import { ITrainData } from "@types";
import { HelpOutlineIcon, InfoOutlinedIcon } from "../../icons";
import { useRecoilState, useResetRecoilState } from "recoil";
import { exceptFileState, serverFileSecondState, serverFileState, tempFileState } from "../../recoil";
import { AnimatePresence, motion } from "framer-motion";
import { getInspModels } from "../../services/modelService";
import { initialProjectData } from "../ProjectPage";
import { countServerFiles, transFiles } from "../../utils/utils";
import CustomPreOption from "../../components/new/CustomPreOption";
import { getFile, getUnassignedDataSets } from "common-lib";
import { useTutorial } from "../../context/TutorialContext";
import { countNgFiles, fillNgForTutorial } from "../../tutorial/ngFill";
import { TUTORIALS } from "../../tutorial";

export default function ProjectCreatePage() {
  return (
    <>
      <Box
        sx={{
          widht: "100%",
          height: "100%",
          alignItems: "center",
          flexDirection: "column",
          pr: "32px",
          pl: "32px",
          pt: "40px",
        }}
      >
        <Typography id="create-project-title" variant="h5" fontWeight={700}>
          프로젝트 생성
        </Typography>
        <Typography variant="body1">새로운 AI 프로젝트를 생성하세요</Typography>
        <CreationStepper />
      </Box>
    </>
  );
}

const steps = ["기본 정보", "데이터셋 설정", "학습 옵션"];
const MIN_NG_COUNT = 10;

interface projectInfoProps {
  step: string;
  project_name: string;
  material_name: string;
}

type FilesMap = { [key: string]: string[] };

function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function createRandomizedFiles(files: FilesMap, randomLabels: string[]): FilesMap {
  // 1. 모든 파일을 하나의 배열로 합침
  const allItems = Object.values(files).flat();

  // 2. 랜덤 셔플
  const shuffled = shuffleArray(allItems);

  // 3. 결과 객체 초기화
  const result: FilesMap = {};
  randomLabels.forEach((label) => {
    result[label] = [];
  });

  // 4. 라벨에 라운드로빈 분배
  shuffled.forEach((item, index) => {
    const label = randomLabels[index % randomLabels.length];
    result[label].push(item);
  });

  // 5. etc 추가
  result["etc"] = [];

  return result;
}

export function CreationStepper() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [isOpenDataset, setIsOpenDataset] = useState<boolean>(false);
  const [serverFiles, setServerFiles] = useRecoilState(serverFileSecondState);
  const [exceptPaths, setExceptPaths] = useRecoilState(exceptFileState); // except.txt (검사 전용: 학습 제외 → valid)
  const resetServerFile = useResetRecoilState(serverFileState);
  const [files, setFiles] = useState<{ [key: string]: string[] }>({
    unlabeled: [],
  });
  const [pollingProjectId, setPollingProjectId] = useState<number | null>(null);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [flatFiles, setFlatFiles] = useState<string[]>([]);
  const { run, nextSubStep, tutorialKeyRef } = useTutorial();

  //   step1
  const [projectInfo, setProjectConfig] = useState<projectInfoProps>(() => {
    const step = "검사";
    return {
      step,
      project_name: `새 ${step} 프로젝트`,
      material_name: "",
    };
  });

  //   step2
  const [dsMode, setDsMode] = useState<"create" | "select">("select");
  const [selectedDsFile, setSelectedDsFile] = useState<DatasetItem | null>(null);
  const [dsFileError, setDsFileError] = useState(false); // 선택한 데이터셋 파일을 읽을 수 없음
  // step3
  const [autoLabeling, setAutoLabeling] = useState(true);
  const [labelCount, setLabelCount] = useState(4);
  const [selectedTrain, setSelectedTrain] = useState<ITrainData | null>(null);
  const [selectedModel, setSelectedModel] = useState<modelItem | null>(null);
  const [autoLabelingConfig, setAutoLabelingConfig] = useState<{
    trainTargetLabel: string;
    trainConditionCount: number;
    autoLabelingCondition: string;
  }>({
    trainTargetLabel: "NG",
    trainConditionCount: 500,
    autoLabelingCondition: "+30",
  });
  const [openCustom, setOpenCustom] = useState(false);
  const preOptions = ["A 알고리즘", "B 알고리즘", "C 알고리즘", "D 알고리즘", "사용자화"];
  const [selectedPreOption, setSelectedPreOption] = useState(preOptions[0]);

  useEffect(() => {
    const merged = Object.values(files).flat();
    setFlatFiles(merged);
  }, [files]);

  // except.txt는 검사 프로젝트에서만 의미가 있음 → 다른 타입으로 바꾸면 비움
  useEffect(() => {
    if (projectInfo.step !== "검사" && exceptPaths.length > 0) setExceptPaths([]);
  }, [projectInfo.step]);

  useEffect(() => {
    const updateFiles = async () => {
      if (dsMode === "select" && selectedDsFile) {
        try {
          setFiles(await fetchDsFile(selectedDsFile.path));
          setDsFileError(false);
        } catch (error) {
          console.error(error);
          setFiles({ unlabeled: [] });
          setDsFileError(true);
        }
        return;
      }
      setDsFileError(false);
      setFiles(transFiles(serverFiles));
    };
    updateFiles();
  }, [dsMode, selectedDsFile, serverFiles]);

  useEffect(() => {
    const selectedTargetLabel = selectedDsFile?.fileData?.class_list?.[0] ?? "OK";
    const clamp = (v: number, max: number) => Math.min(v, max);
    console.log("dsMode", dsMode);
    setAutoLabelingConfig((prev) => {
      if (dsMode === "create") {
        const total = serverFiles.length;
        const isUnlabeled = (label: string) => label === "" || label === "unlabeled";
        const unlabeledCount = serverFiles.filter((f) => isUnlabeled(f.label)).length;
        const labeledCount = total - unlabeledCount;

        let trainConditionCount: number;

        if (unlabeledCount === 0) {
          setAutoLabeling(false);
          trainConditionCount = total;
        } else {
          setAutoLabeling(true);

          if (unlabeledCount <= 10) {
            trainConditionCount = total;
          } else {
            trainConditionCount = clamp(labeledCount + 10, total);
          }
        }

        return {
          trainTargetLabel: selectedTargetLabel,
          trainConditionCount,
          autoLabelingCondition: "+30",
        };
      }

      // dsMode === "select"
      if (dsMode === "select" && selectedDsFile) {
        const counts = selectedDsFile.fileData?.counts ?? {};

        const total =
          selectedDsFile.fileData?.total_files ??
          Object.values(counts).reduce((a: number, b: number) => a + Number(b), 0);

        const unlabeledCount = Number(counts.unlabeled ?? 0);
        const labeledCount = total - unlabeledCount;

        let trainConditionCount: number;

        if (unlabeledCount === 0) {
          setAutoLabeling(false);
          trainConditionCount = total;
        } else {
          setAutoLabeling(true);

          if (unlabeledCount <= 10) {
            trainConditionCount = total;
          } else {
            trainConditionCount = clamp(labeledCount + 10, total);
          }
        }

        return {
          trainTargetLabel: selectedTargetLabel,
          trainConditionCount,
          autoLabelingCondition: "+30",
        };
      }

      return prev;
    });
    console.log(selectedDsFile);
  }, [dsMode, selectedDsFile, serverFiles]);

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const fetchDsFile = async (path: string) => {
    const dsFileResponse = await getFile(path);
    const data = dsFileResponse?.data;
    // ponytail: 데이터셋 파일 유효성 판정은 여기 한 곳에서만
    const isDsFile =
      !!data && typeof data === "object" && ["all", "train", "valid", "unlabeled"].some((k) => Array.isArray(data[k]));
    if (!isDsFile) throw new Error(`유효한 데이터셋 파일이 아닙니다: ${path}`);
    return extractImgPaths(data);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNext = async () => {
    if (!projectInfo.material_name || projectInfo.material_name.trim() === "") {
      toast.warn("자재명은 필수 입력값입니다.");
      return;
    }

    if (activeStep === 0) {
      console.log(activeStep);
      try {
        await checkProjectName(projectInfo.project_name);
      } catch (error: any) {
        toast.warn(error.message);
        return;
      }
    }

    if (activeStep === 1 && dsMode === "select" && (!selectedDsFile || dsFileError)) {
      toast.warn("사용할 수 없는 데이터셋 파일입니다. 다른 파일을 선택해주세요.");
      return;
    }

    // 검사 프로젝트는 NG 샘플이 최소 10장 있어야 학습이 가능
    if (activeStep === 1 && projectInfo.step === "검사") {
      let ngCount = countNgFiles(files);

      if (ngCount < MIN_NG_COUNT && run && tutorialKeyRef.current === "inspection-project") {
        const filled = fillNgForTutorial(files, MIN_NG_COUNT - ngCount);
        if (filled) {
          setFiles(filled);
          ngCount = countNgFiles(filled);
        }
      }

      if (ngCount < MIN_NG_COUNT) {
        toast.warn(`검사 프로젝트는 NG 이미지가 최소 ${MIN_NG_COUNT}장 필요합니다. (현재 ${ngCount}장)`);
        return;
      }
    }

    if (activeStep === 2 && projectInfo.step === "분석") {
      console.log(flatFiles);
    }

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
    if (run) {
      if (activeStep === 1) {
        setTimeout(() => nextSubStep(), 100);
      }
    }
  };

  const isEmptyFiles = (files: { [key: string]: string[] }) => Object.values(files).every((arr) => arr.length === 0);

  const handleSave = async () => {
    let updateFiles = files;
    if (selectedPreOption !== "사용자화" && projectInfo.step === "분석") {
      const randomLabels =
        labelCount > 0 ? Array.from({ length: labelCount }, (_, i) => String.fromCharCode(65 + i)) : ["OK", "NG"];
      updateFiles = createRandomizedFiles(files, randomLabels);
      setFiles(updateFiles);
      console.log(updateFiles);
    }

    if (updateFiles && isEmptyFiles(updateFiles)) {
      toast.warn("선택된 이미지들이 없습니다");
      return;
    }
    if (activeStep) {
      const projectData: IAddProject = {
        ...projectInfo,
        dataSet_id: dsMode === "select" ? selectedDsFile?.id : null,
        file_path: dsMode === "select" ? selectedDsFile?.path : null,
        files: updateFiles,
        create_at: new Date(),
        class_list:
          labelCount > 0 ? Array.from({ length: labelCount }, (_, i) => String.fromCharCode(65 + i)) : ["OK", "NG"],
        inspModel_id: selectedModel ? selectedModel.id : null,
        config: {
          ...initialProjectData.config,
          keyConfig: {
            labeling:
              labelCount > 0 && projectInfo.step === "분석"
                ? Array.from({ length: labelCount }, (_, i) => (i + 1).toString())
                : ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
            nav: {
              prevImage: "ArrowUp",
              nextImage: "ArrowDown",
            },
            action: {
              reset: "Backspace",
              undo: { key: "z", modifier: "Ctrl" },
              save: { key: "s", modifier: "Ctrl" },
              selectAll: { key: "a", modifier: "Ctrl" },
            },
          },
          autoLabeling: autoLabeling,
          trainTargetLabel: autoLabelingConfig.trainTargetLabel,
          trainConditionCount: autoLabelingConfig.trainConditionCount,
          autoLabelingCondition: autoLabelingConfig.autoLabelingCondition,
        },
        train_id: selectedTrain?.id,
        // 검사: except.txt에 적힌 이미지는 train에서 빼고 valid로
        except_files: projectInfo.step === "검사" ? exceptPaths : [],
      };
      console.log(projectData);
      const response = await createProject(projectData);
      const id = response.project_id;

      const total = Object.values(updateFiles).flat().length;
      setTotalFileCount(total);
      setPollingProjectId(id);

      if (projectInfo.step === "분석") {
        navigate(`/main/project/${id}/analysis`, {
          state: { totalFileCount: Object.values(updateFiles).flat().length },
        });
      } else {
        navigate(`/main/project/${id}`, {
          state: { totalFileCount: Object.values(updateFiles).flat().length },
        });
      }

      resetServerFile();
      setExceptPaths([]);
      toast.success(t("SuccessSaveMsg"));
    }
  };

  const stepComponents = [
    <StepProjectConfig projectInfo={projectInfo} setProjectConfig={setProjectConfig} />,
    <StepDSConfig
      type={projectInfo.step}
      dsMode={dsMode}
      setDsMode={setDsMode}
      selectedDsFile={selectedDsFile}
      setSelectedDsFile={setSelectedDsFile}
      dsFileError={dsFileError}
      setIsOpenDataset={setIsOpenDataset}
    />,
    <StepModelConfig
      step={projectInfo.step}
      dsMode={dsMode}
      autoLabeling={autoLabeling}
      setAutoLabeling={setAutoLabeling}
      selectedTrain={selectedTrain}
      setSelectedTrain={setSelectedTrain}
      selectedModel={selectedModel}
      setSelectedModel={setSelectedModel}
      labelCount={labelCount}
      setLabelCount={setLabelCount}
      autoLabelingConfig={autoLabelingConfig}
      setAutoLabelingConfig={setAutoLabelingConfig}
      flatFiles={flatFiles}
      setFiles={setFiles}
      openCustom={openCustom}
      setOpenCustom={setOpenCustom}
      preOptions={preOptions}
      selectedPreOption={selectedPreOption}
      setSelectedPreOption={setSelectedPreOption}
      selectedDsFile={selectedDsFile}
    />,
  ];

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};

          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === steps.length - 1 ? (
        <React.Fragment>
          <Typography sx={{ mt: 2, mb: 1 }}>
            <Box
              sx={{
                backgroundColor: "var(--dk-surface, #fff)",
                borderRadius: 2,
                height: "fit-content",
                widht: "100%",
                mt: 3,
                pr: 4,
                pl: 4,
                pt: 3,
                pb: 3,
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
              }}
            >
              {stepComponents[activeStep]}
            </Box>
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              이전
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button id="finish-btn" onClick={handleSave}>
              Finish
            </Button>
          </Box>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Box
            sx={{
              backgroundColor: "var(--dk-surface, #fff)",
              borderRadius: 2,
              height: "fit-content",
              widht: "100%",
              mt: 3,
              pr: 4,
              pl: 4,
              pt: 3,
              pb: 3,
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
            }}
          >
            {stepComponents[activeStep]}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button color="inherit" disabled={activeStep == 0} onClick={handleBack} sx={{ mr: 1 }}>
              이전
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button id="next-btn" onClick={handleNext}>
              {activeStep === steps.length - 1 ? "Finish" : "Next"}
            </Button>
          </Box>
        </React.Fragment>
      )}
      {
        isOpenDataset && (
          // (projectInfo.step === "분류" ? (
          <Modal id="dataset-modal" open={isOpenDataset} onClose={() => setIsOpenDataset(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                boxShadow: 24,
                width: "fit-content",
                userSelect: "none",
                bgcolor: "var(--dk-surface, #fff)",
              }}
            >
              <ServerFileModal step={projectInfo.step} onClick={() => setIsOpenDataset(false)} />
            </Box>
          </Modal>
        )
        // ) : (
        //   <Modal open={isOpenDataset} onClose={() => setIsOpenDataset(false)}>
        //     <Box
        //       sx={{
        //         position: "absolute",
        //         top: "50%",
        //         left: "50%",
        //         transform: "translate(-50%, -50%)",
        //         boxShadow: 24,
        //         width: "fit-content",
        //         userSelect: "none",
        //         bgcolor: "background.paper",
        //       }}
        //     >
        //       <AnalysisDataset onClick={() => setIsOpenDataset(false)} />
        //     </Box>
        //   </Modal>
        // ))
      }
    </Box>
  );
}

interface StepProjectConfigProps {
  projectInfo: projectInfoProps;
  setProjectConfig: React.Dispatch<React.SetStateAction<projectInfoProps>>;
}

function StepProjectConfig({ projectInfo, setProjectConfig }: StepProjectConfigProps) {
  const { run, nextSubStep, tutorialKey, stepIndexRef } = useTutorial();

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const step = e.target.value;

    setProjectConfig((prev) => ({
      ...prev,
      step,
      project_name: `새 ${step} 프로젝트`,
    }));

    if (run) {
      const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];
      if (currentSubStep?.nextTrigger === "#material-name-input") {
        nextSubStep();
      }
    }
  };

  const updateField = (field: keyof typeof projectInfo) => (value: string) =>
    setProjectConfig({ ...projectInfo, [field]: value });

  return (
    <Box sx={{ flexDirection: "column" }}>
      <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>기본 정보 입력</Typography>
      <Box sx={{ p: "10px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="body2">프로젝트 타입</Typography>
            <RadioGroup row value={projectInfo.step} onChange={handleTypeChange}>
              <FormControlLabel id="type-insp" value="검사" control={<Radio />} label="검사" />
              <FormControlLabel id="type-cls" value="분류" control={<Radio />} label="분류" />
              <FormControlLabel id="type-ana" value="분석" control={<Radio />} label="분석" />
            </RadioGroup>
          </Box>

          <CommonInput
            id="project-name"
            label="프로젝트명"
            value={projectInfo.project_name}
            setValue={updateField("project_name")}
          />

          <CommonInput
            id="material-name"
            label="자재명"
            value={projectInfo.material_name}
            setValue={updateField("material_name")}
            required
            placeholder="자재명을 입력하세요"
          />
        </Box>
      </Box>
    </Box>
  );
}

interface StepDsConfigProps {
  type: string;
  dsMode: "create" | "select";
  setDsMode: (mode: "create" | "select") => void;
  selectedDsFile: DatasetItem | null;
  setSelectedDsFile: (file: DatasetItem | null) => void;
  setIsOpenDataset: (open: boolean) => void;
  dsFileError: boolean;
}

function StepDSConfig({
  type,
  dsMode,
  setDsMode,
  selectedDsFile,
  setSelectedDsFile,
  setIsOpenDataset,
  dsFileError,
}: StepDsConfigProps) {
  const [unassignedList, setUnassignedList] = useState<DatasetItem[]>([]);
  const [serverFiles, setServerFiles] = useRecoilState(serverFileSecondState);
  const [tempFiles, setTempFiles] = useRecoilState(tempFileState); // (오른쪽) 데이터셋으로 설정할 선택된 이미지들 - 확정 전

  useEffect(() => {
    console.log("serverFiles", serverFiles);
  }, [serverFiles]);

  useEffect(() => {
    console.log("tempFiles", tempFiles);
  }, [tempFiles]);

  const handleDatasetModeChange = (mode: "create" | "select") => {
    setDsMode(mode);
    if (mode === "select") {
      fetchUnassignedDatasets();
    } else {
      setIsOpenDataset(true);
    }
  };

  const fetchUnassignedDatasets = async () => {
    try {
      const response = await getUnassignedDataSets();

      const filtered =
        type === "분류"
          ? response.dataSets.filter((item: DatasetItem) => {
              const classList = item.fileData?.class_list ?? [];
              return classList.length === 2 && classList.includes("OK") && classList.includes("NG");
            })
          : response.dataSets;

      setUnassignedList(filtered);
      setSelectedDsFile(filtered[0]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (dsMode === "select") {
      fetchUnassignedDatasets();
    }
  }, []);

  const handleSelectFile = (item: DatasetItem) => setSelectedDsFile(item);

  return (
    <Box sx={{ flexDirection: "column" }}>
      <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>데이터셋 설정</Typography>
      <Box sx={{ p: "10px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <button
                id="select-dataset-file"
                onClick={() => handleDatasetModeChange("select")}
                className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                  dsMode === "select"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                }`}
              >
                <div className="text-center">
                  <Folder className="w-5 h-5 mx-auto mb-2" />
                  <span className="font-medium">기존 선택</span>
                </div>
              </button>
              <button
                id="create-dataset-btn"
                onClick={() => {
                  handleDatasetModeChange("create");
                }}
                className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                  dsMode === "create"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                }`}
              >
                <div className="text-center">
                  <Upload className="w-5 h-5 mx-auto mb-2" />
                  <span className="font-medium">새로 만들기</span>
                </div>
              </button>
            </Box>
            {dsMode === "select" && (
              <Box sx={{ width: "100%", display: "flex", height: "100%", gap: 1 }}>
                <Box sx={{ mt: 1, mb: 1, width: "70%", height: "380px" }}>
                  <List id="dataset-file-list" sx={{ overflowY: "auto", height: "100%" }}>
                    {unassignedList.map((item, id) => (
                      <ListItem
                        key={id}
                        id={`dataset-item-${item.id}`}
                        sx={{
                          cursor: "pointer",
                          borderBottom: "1px solid var(--dk-border, #dddddd)",
                          backgroundColor:
                            selectedDsFile && selectedDsFile.path === item.path ? "#e3f2fd" : "transparent",
                          "&:hover": {
                            backgroundColor:
                              selectedDsFile && selectedDsFile.path === item.path ? "#bbdefb" : "#f5f5f5",
                          },
                        }}
                        onClick={() => handleSelectFile(item)}
                      >
                        {item.name}
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Box
                  sx={{
                    backgroundColor: "var(--info-bgcolor)",
                    borderRadius: 2,
                    width: "30%",
                    height: "380px",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: selectedDsFile && !dsFileError ? "flex-start" : "center",
                    alignItems: selectedDsFile && !dsFileError ? "flex-start" : "center",
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  {dsFileError ? (
                    <>
                      <Typography variant="h6" sx={{ color: "error.main", fontWeight: 600, mb: 1 }}>
                        해당 데이터셋 파일은 사용할 수 없습니다
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", textAlign: "center", wordBreak: "break-all" }}
                      >
                        파일이 없거나 형식이 올바르지 않습니다. 다른 파일을 선택해주세요.
                      </Typography>
                    </>
                  ) : selectedDsFile ? (
                    <>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontWeight: 600,
                          mb: 2,
                          wordBreak: "break-all",
                          lineHeight: 1.4,
                        }}
                      >
                        {selectedDsFile?.name}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid var(--dk-border, #e0e0e0)",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        {/* 클래스 */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            px: "14px",
                            py: "10px",
                            bgcolor: "var(--dk-surface-2, #fafafa)",
                            borderBottom: "1px solid var(--dk-border, #e0e0e0)",
                          }}
                        >
                          <Typography sx={{ fontSize: "12px", color: "#757575", minWidth: "44px" }}>클래스</Typography>
                          <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                            {(selectedDsFile?.fileData?.class_list ?? []).map((cls) => (
                              <Chip
                                key={cls}
                                label={cls}
                                size="small"
                                sx={{
                                  height: "22px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  borderRadius: "99px",
                                  border: "1px solid",
                                  ...(cls === "OK"
                                    ? { bgcolor: "#f1f8e9", color: "#33691e", borderColor: "#aed581" }
                                    : cls === "NG"
                                      ? { bgcolor: "#fce4ec", color: "#b71c1c", borderColor: "#ef9a9a" }
                                      : { bgcolor: "#e3f2fd", color: "#0d47a1", borderColor: "#90caf9" }),
                                  "& .MuiChip-label": { px: "10px" },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* 라벨링 */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            px: "14px",
                            py: "10px",
                            borderBottom: "1px solid var(--dk-border, #e0e0e0)",
                          }}
                        >
                          <Typography sx={{ fontSize: "12px", color: "#757575", minWidth: "44px" }}>라벨링</Typography>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#212121" }}>
                              {selectedDsFile?.fileData?.labeled_files ?? "-"}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "#bdbdbd" }}>/</Typography>
                            <Typography sx={{ fontSize: "12px", color: "#9e9e9e" }}>
                              {selectedDsFile?.fileData?.total_files ?? "-"}장
                            </Typography>
                          </Box>
                        </Box>

                        {/* 타입 */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            px: "14px",
                            py: "10px",
                            bgcolor: "var(--dk-surface-2, #fafafa)",
                          }}
                        >
                          <Typography sx={{ fontSize: "12px", color: "#757575", minWidth: "44px" }}>타입</Typography>
                          <Chip
                            label={selectedDsFile?.fileData?.type || "-"}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "11px",
                              fontWeight: 500,
                              borderRadius: "99px",
                              bgcolor: "var(--dk-surface-2, #f5f5f5)",
                              color: "#424242",
                              border: "1px solid var(--dk-border, #e0e0e0)",
                              "& .MuiChip-label": { px: "10px" },
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        선택된 데이터셋 파일이 없습니다
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                        }}
                      >
                        좌측 목록에서 데이터셋 파일을 선택해주세요.
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            )}
            {dsMode === "create" && (
              <Box
                id="selected-file-info"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  p: 2,
                  borderRadius: 3,
                  background: "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                {/* 상단 요약 */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#4f46e5",
                      }}
                    />

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      선택된 파일
                    </Typography>

                    <Chip
                      label={`${serverFiles.length}개`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: "#4f46e5",
                        color: "#fff",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    클래스별 파일 분포
                  </Typography>
                </Box>

                {/* 클래스 리스트 */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.2,
                  }}
                >
                  {Object.entries(countServerFiles(serverFiles)).map(([label, count]) => {
                    const filesForLabel = serverFiles.filter((f) => f.label === label);

                    const previewList = filesForLabel.slice(0, 5);
                    const extraCount = filesForLabel.length - previewList.length;

                    const tooltipContent = (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          maxWidth: "400px",
                          p: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            mb: 0.5,
                          }}
                        >
                          {label}
                        </Typography>

                        {previewList.map((f) => (
                          <Typography
                            key={f.id}
                            variant="caption"
                            sx={{
                              wordBreak: "break-all",
                              opacity: 0.9,
                            }}
                          >
                            {f.path}
                          </Typography>
                        ))}

                        {extraCount > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.5,
                              fontWeight: 700,
                              color: "#a5b4fc",
                            }}
                          >
                            + 그 외 {extraCount}개
                          </Typography>
                        )}
                      </Box>
                    );

                    return (
                      <Tooltip key={label} title={tooltipContent} arrow placement="top">
                        <Box
                          sx={{
                            minWidth: "140px",
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "rgba(79, 70, 229, 0.08)",
                            border: "1px solid rgba(79, 70, 229, 0.18)",
                            transition: "0.2s ease",
                            cursor: "pointer",

                            "&:hover": {
                              transform: "translateY(-2px)",
                              backgroundColor: "rgba(79, 70, 229, 0.14)",
                              boxShadow: "0 4px 12px rgba(79,70,229,0.15)",
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            CLASS
                          </Typography>

                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              mb: 0.3,
                              wordBreak: "break-word",
                            }}
                          >
                            {label}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              color: "#6366f1",
                              fontWeight: 600,
                            }}
                          >
                            {count} files
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface trainModelItem {
  id: number;
  name: string;
  path: string;
}

interface modelItem {
  id: number;
  uuid: string;
  name: string;
  description: string | null;
  options: string | null;
  pre_process: string | null;
  post_process: string | null;
  info: string | null;
  trainModel: trainModelItem;
  create_at: string;
}

interface StepModelConfigProps {
  step: string;
  dsMode: "create" | "select";
  autoLabeling: boolean;
  setAutoLabeling: (auto: boolean) => void;
  selectedTrain: ITrainData | null;
  setSelectedTrain: (train: ITrainData | null) => void;
  selectedModel: modelItem | null;
  setSelectedModel: (model: modelItem | null) => void;
  labelCount: number;
  setLabelCount: (count: number) => void;
  autoLabelingConfig: {
    trainTargetLabel: string;
    trainConditionCount: number;
    autoLabelingCondition: string;
  };
  setAutoLabelingConfig: React.Dispatch<
    React.SetStateAction<{
      trainTargetLabel: string;
      trainConditionCount: number;
      autoLabelingCondition: string;
    }>
  >;
  flatFiles: string[];
  setFiles: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  openCustom: boolean;
  setOpenCustom: (open: boolean) => void;
  preOptions: string[];
  selectedPreOption: string;
  setSelectedPreOption: (option: string) => void;
  selectedDsFile: DatasetItem | null;
}

function StepModelConfig({
  step,
  dsMode,
  autoLabeling,
  setAutoLabeling,
  selectedTrain,
  setSelectedTrain,
  selectedModel,
  setSelectedModel,
  labelCount,
  setLabelCount,
  autoLabelingConfig,
  setAutoLabelingConfig,
  flatFiles,
  setFiles,
  openCustom,
  setOpenCustom,
  preOptions,
  selectedPreOption,
  setSelectedPreOption,
  selectedDsFile,
}: StepModelConfigProps) {
  const [modelList, setModelList] = useState<modelItem[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [serverFiles, setServerFiles] = useRecoilState(serverFileSecondState);
  const { run, nextSubStep, currentStep, stepIndex } = useTutorial();

  const [filter, setFilter] = useState({
    type: "all" as "all" | "default" | "continued",
    overkillEnabled: false,
    overkillValue: "",
  });
  const [editValues, setEditValues] = useState({
    trainTargetLabel: autoLabelingConfig.trainTargetLabel,
    trainConditionCount: autoLabelingConfig.trainConditionCount,
    autoLabelingCondition: autoLabelingConfig.autoLabelingCondition,
  });

  // TextField 변경 핸들러
  const handleEditChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };

  // 저장 처리
  const handleSave = () => {
    setAutoLabelingConfig(editValues);
    setIsEditing(false);
  };

  // 취소 처리
  const handleCancel = () => {
    setEditValues(autoLabelingConfig);
    setIsEditing(false);
  };

  const fetchInspModelList = async () => {
    try {
      const response = await getInspModels();
      // 에폭학습 중간 산출물("...Epoch N")은 학습 옵션에서 고를 수 없게 거른다
      const list = (response.data.contents as modelItem[]).filter((m) => !nameHas(m, "Epoch"));
      setModelList(list);
      setSelectedModel(pickModel(list, "default", step));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInspModelList();
  }, []);

  const handleToggle = () => {
    console.log("serverFiles", serverFiles);
    if (!autoLabeling) {
      if (dsMode === "create") {
        const hasUnlabeled = serverFiles.some((f) => f.label === "" || f.label === "unlabeled");

        if (!hasUnlabeled) {
          toast.error("모든 데이터가 라벨링 되어있어 자동 라벨링을 켤 수 없습니다.");
          return;
        }
      }

      if (dsMode === "select") {
        const counts = selectedDsFile?.fileData?.counts;
        const unlabeled = Number(counts?.unlabeled ?? 0);

        if (unlabeled === 0) {
          toast.error("unlabeled 데이터가 없어 자동 라벨링을 켤 수 없습니다.");
          return;
        }
      }
    }
    setAutoLabeling(!autoLabeling);
  };

  // 새로 학습/이어학습 선택 시 조건에 맞는 모델 자동 선택
  const handleFilterType = (type: "all" | "default" | "continued") => {
    setFilter((prev) => ({ ...prev, type }));
    if (type === "all") return;
    const picked = pickModel(modelList, type, step);
    if (picked) setSelectedModel(picked);
    else toast.error(type === "continued" ? "이어학습 가능한 모델이 없습니다." : "사용 가능한 기본 모델이 없습니다.");
  };

  const handleSelectModel = (item: modelItem) => {
    setSelectedModel(item);
    if (run) {
      setTimeout(() => nextSubStep(), 100);
    }
  };

  const filteredList = modelList.filter((item) => {
    const matchSearch = item.name.toLowerCase().includes(searchText.toLowerCase());

    const info = getInfo(item);
    const type = getModelType(item);

    const matchType =
      filter.type === "all" ||
      (filter.type === "default" && type === "default") ||
      (filter.type === "continued" && type === "continued");

    let matchOverkill = true;
    if (filter.overkillEnabled) {
      if (!info || info?.performance?.overkill === undefined) return false;

      const percent = info.performance.overkill * 100;
      const limit = Number(filter.overkillValue);

      matchOverkill = percent <= limit;
    }

    return matchSearch && matchType && matchOverkill;
  });
  return (
    <Box sx={{ flexDirection: "column" }}>
      <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>학습 옵션 설정</Typography>
      <Box
        sx={{
          p: "10px",
          overflowY: "auto",
          height: "fit-content",
          maxHeight: "500px",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {step != "검사" && (
          <Box
            id="train-option-setting"
            sx={{
              border: "1px solid #dcdcdc",
              borderRadius: "8px",
              backgroundColor: "var(--dk-surface-2, #fafafa)",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                p: 2,
              }}
            >
              <Box
                id="auto-train-setting"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>자동 라벨링 설정</Typography>

                <ToggleButtonGroup value={autoLabeling} exclusive onChange={handleToggle} size="small">
                  <ToggleButton value={true} sx={{ width: "86px" }}>
                    ON
                  </ToggleButton>
                  <ToggleButton value={false} sx={{ width: "86px" }}>
                    OFF
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                조건 충족 시 자동으로 라벨링을 시작합니다. (권장)
              </Typography>
            </Box>
            <AnimatePresence>
              {autoLabeling && (
                <motion.div
                  key="autoLearningBox"
                  initial={{ opacity: 0, y: -10 }} // 처음 등장: 약간 위에서 ↓ 내려오며 나타남
                  animate={{ opacity: 1, y: 0 }} // 나타날 때 애니메이션
                  exit={{ opacity: 0, y: -30 }} // 사라질 때: 위로 올라가며 fade-out
                  transition={{ duration: 0.25 }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "var(--secondary-bg-color)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        color: "var(--main-text-color)",
                      }}
                    >
                      <Box sx={{ display: "flex", mb: 1, alignItems: "center" }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <Typography sx={{ fontWeight: 600 }}>기본설정</Typography>
                      </Box>

                      {!isEditing ? (
                        <>
                          {step !== "분석" && (
                            <Typography>자동 라벨링 타겟 라벨: {autoLabelingConfig.trainTargetLabel}</Typography>
                          )}
                          <Typography>자동 라벨링 최소 수량: {autoLabelingConfig.trainConditionCount}장</Typography>
                          <Typography>자동 라벨링 조건: {autoLabelingConfig.autoLabelingCondition}</Typography>
                        </>
                      ) : (
                        // 수정 모드 UI
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            mt: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            label="자동 라벨링 타겟 라벨"
                            name="trainTargetLabel"
                            value={editValues.trainTargetLabel}
                            onChange={handleEditChange}
                          />
                          <TextField
                            size="small"
                            label="최소 수량"
                            name="trainConditionCount"
                            type="number"
                            value={editValues.trainConditionCount}
                            onChange={handleEditChange}
                          />
                          <TextField
                            size="small"
                            label="자동 라벨링 조건 (+30, *2 등)"
                            name="autoLabelingCondition"
                            value={editValues.autoLabelingCondition}
                            onChange={handleEditChange}
                          />
                        </Box>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        color: "var(--main-text-color)",
                      }}
                    >
                      {!isEditing ? (
                        <>
                          <Typography>설정을 변경하시겠습니까?</Typography>
                          <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
                            변경하기
                          </Button>
                        </>
                      ) : (
                        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                          <Button size="small" onClick={handleCancel}>
                            취소
                          </Button>
                          <Button size="small" variant="contained" onClick={handleSave}>
                            저장
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        )}
        {step === "분석" && (
          <Box sx={{ mt: 2, pr: 1, pl: 1 }}>
            <Divider />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
                gap: "3px",
              }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>분류 클래스 수 설정</Typography>
              <Tooltip title="설정한 라벨 개수에 맞춰 이미지가 자동으로 분류됩니다">
                <InfoIcon size="16px" />
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                id="input-class-count"
                value={labelCount}
                inputProps={{ max: 9 }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  e.preventDefault();
                  const value = e.target.value;
                  const last = value.slice(-1);
                  if (!/^[0-9]$/.test(last)) return;

                  setLabelCount(Number(last));
                }}
              />
            </Box>

            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
                gap: "3px",
              }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
                사전분류 옵션
              </Typography>
              <Select
                value={selectedPreOption}
                onChange={(e) => {
                  setSelectedPreOption(e.target.value);
                }}
                size="small"
              >
                {preOptions.map((name, index) => (
                  <MenuItem key={index} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
              {selectedPreOption === "사용자화" && (
                <Button onClick={() => setOpenCustom(true)}>
                  자세히 <ArrowRight />
                </Button>
              )}
            </Box> */}
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Box
            id="select-model"
            sx={{
              border: "1px solid #dcdcdc",
              borderRadius: "8px",
              backgroundColor: "var(--dk-surface-2, #fafafa)",
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>모델 선택</Typography>
                <Tooltip title="default는 학습이 이루어지지 않은 기본 옵션입니다.">
                  <HelpOutlineIcon />
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  선택된 모델
                </Typography>
                {selectedModel ? (
                  <SelectedModelChip model={selectedModel} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    없음
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 1,
                mt: 1.5,
                mb: 1.5,
                flexWrap: "wrap",
              }}
            >
              <ToggleButtonGroup
                value={filter.type}
                exclusive
                size="small"
                onChange={(_, value) => value && handleFilterType(value)}
                sx={{ bgcolor: "var(--dk-surface, #fff)" }}
              >
                <ToggleButton value="default">새로 학습</ToggleButton>
                <ToggleButton value="continued">이어학습</ToggleButton>
                <ToggleButton value="all">전체</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                size="small"
                placeholder="모델 검색..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 180, bgcolor: "var(--dk-surface, #fff)" }}
              />
              <Box sx={{ position: "relative" }}>
                <Button
                  onClick={() => setFilterOpen((prev) => !prev)}
                  variant={filter.overkillEnabled ? "contained" : "outlined"}
                  sx={{ minWidth: "40px", height: "40px", px: 0 }}
                >
                  <FilterIcon />
                </Button>

                {filterOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 45,
                      right: 0,
                      width: 220,
                      bgcolor: "var(--dk-surface, #fff)",
                      border: "1px solid var(--dk-border, #ddd)",
                      borderRadius: 2,
                      boxShadow: 3,
                      p: 2,
                      zIndex: 10,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>모델 필터</Typography>

                    <Box sx={{ mt: 1 }}>
                      <Typography fontWeight={600} fontSize={14}>
                        Overkill 필터
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filter.overkillEnabled}
                            onChange={(e) =>
                              setFilter({
                                ...filter,
                                overkillEnabled: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Overkill 기준 사용"
                      />

                      {filter.overkillEnabled && (
                        <TextField
                          size="small"
                          type="number"
                          placeholder="예: 5"
                          value={filter.overkillValue}
                          onChange={(e) =>
                            setFilter({
                              ...filter,
                              overkillValue: e.target.value,
                            })
                          }
                          InputProps={{
                            endAdornment: <Typography>%</Typography>,
                          }}
                          sx={{ mt: 1 }}
                          fullWidth
                        />
                      )}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Button
                      size="small"
                      fullWidth
                      onClick={() =>
                        setFilter({
                          type: "all",
                          overkillEnabled: false,
                          overkillValue: "",
                        })
                      }
                    >
                      필터 초기화
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <List
              id="model-list"
              disablePadding
              sx={{
                overflowY: "auto",
                maxHeight: "420px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 1.5,
                pr: 0.5,
              }}
            >
              {(filteredList.length > 0 || searchText !== "" ? filteredList : modelList).map((item, id) => (
                <CustomListItem key={id} item={item} selectedModel={selectedModel} onSelect={handleSelectModel} />
              ))}

              {filteredList.length === 0 && searchText !== "" && (
                <Typography sx={{ color: "var(--dk-text-3, #888)", textAlign: "center", py: 4, gridColumn: "1 / -1" }}>
                  검색 결과가 없습니다.
                </Typography>
              )}
            </List>
          </Box>
        </Box>
      </Box>
      {openCustom && (
        <Modal open={openCustom} onClose={() => setOpenCustom(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              boxShadow: 24,
              width: "80%",
              height: "80%",
              userSelect: "none",
              bgcolor: "background.paper",
              p: 2,
            }}
          >
            <Typography variant="h6">사전분류 데이터 수정</Typography>
            <Divider sx={{ mb: 1 }} />
            <CustomPreOption flatFiles={flatFiles} setFiles={setFiles} labelCount={labelCount} />
          </Box>
        </Modal>
      )}
    </Box>
  );
}

interface CustomListItemProps {
  item: modelItem;
  selectedModel: modelItem | null;
  onSelect: (item: modelItem) => void;
}

const getModelType = (model: modelItem) => {
  const info = getInfo(model);

  if (!info?.parentModel) return "default";
  return "continued";
};

// 앞에 있는 키워드부터 찾고, 하나도 없으면 조건에 맞는 기본 모델 중 랜덤으로 선택된다.
const PREFERRED_MODEL_KEYWORDS = ["train_TopNIR-2", "train_TopNIR"];

const nameHas = (model: modelItem, keyword: string) => model.name.toLowerCase().includes(keyword.toLowerCase());

const getOverkill = (model: modelItem) => getInfo(model)?.performance?.overkill ?? Infinity;

// 새로 학습: 분석=multi 통합 모델 / 그 외=multi 제외, 둘 다 PREFERRED_MODEL_KEYWORDS 우선
// 이어학습: 과검률이 가장 낮은 모델
const pickModel = (list: modelItem[], type: "default" | "continued", step: string): modelItem | null => {
  const pool = list.filter((m) => getModelType(m) === type);
  if (type === "continued") return [...pool].sort((a, b) => getOverkill(a) - getOverkill(b))[0] ?? null;

  const byStep = pool.filter((m) => (step === "분석" ? nameHas(m, "multi") : !nameHas(m, "multi")));
  const candidates = byStep.length ? byStep : pool;
  const final =
    PREFERRED_MODEL_KEYWORDS.map((k) => candidates.filter((m) => nameHas(m, k))).find((list) => list.length) ??
    candidates;

  return final[Math.floor(Math.random() * final.length)] ?? null;
};

const getInfo = (model: modelItem): InfoItem | null => {
  if (!model.info) return null;

  try {
    return JSON.parse(model.info) as InfoItem;
  } catch {
    return null;
  }
};

const SelectedModelChip = ({ model }: { model: modelItem }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const info = getInfo(model);
  const percent = (v?: number) => (typeof v === "number" ? `${Number((v * 100).toFixed(2))}%` : "-");

  const rows: [string, string][] = [
    ["종류", getModelType(model) === "default" ? "기본 모델" : "이어 학습"],
    ["설명", model.description || "설명 없음"],
    ["과검률", percent(info?.performance?.overkill)],
    ["미검률", percent(info?.performance?.missing)],
    ["Threshold", info?.performance?.threshold?.toString() ?? "-"],
    ["학습 epoch", info?.epoch?.toString() ?? "-"],
    ["학습 이미지 수", info?.trained_count?.toString() ?? "-"],
  ];

  return (
    <>
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        label={model.name}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ ml: 1, maxWidth: 260, cursor: "pointer" }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>{model.name}</Typography>
          <Divider sx={{ mb: 1 }} />
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.3 }}>
              <Typography sx={{ fontSize: 13, color: "var(--dk-text-3, #888)" }}>{label}</Typography>
              <Typography sx={{ fontSize: 13, textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

const CustomListItem = ({ item, selectedModel, onSelect }: CustomListItemProps) => {
  const isSelected = selectedModel && selectedModel.id === item.id;
  const type = getModelType(item);
  const info = getInfo(item);

  const overkillRaw = info?.performance?.overkill;
  const hasOverkill = overkillRaw !== undefined;

  // 반올림은 100을 곱한 뒤에 (먼저 반올림하면 0.4737 * 100 = 47.370000000000005)
  const overkillPercent = hasOverkill ? Number((overkillRaw * 100).toFixed(2)) : 0;

  return (
    <ListItem
      id={`model-item-${item.id}`}
      key={item.id}
      onClick={() => onSelect(item)}
      sx={{
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0.75,
        height: "100%",
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "var(--dk-border, #e5e5e5)",
        boxShadow: isSelected ? "0 0 0 1px var(--mui-palette-primary-main, #1976d2) inset" : "none",
        backgroundColor: isSelected ? "primary.50" : "var(--dk-surface, #fff)",
        transition: "all 0.15s ease",

        "&:hover": {
          borderColor: "primary.main",
          transform: "translateY(-1px)",
          boxShadow: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography
          fontWeight={600}
          fontSize={14}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={item.name}
        >
          {item.name}
        </Typography>
        <Chip
          size="small"
          label={type === "default" ? "기본 모델" : "이어 학습"}
          color={type === "default" ? "primary" : "secondary"}
          variant={isSelected ? "filled" : "outlined"}
          sx={{ flexShrink: 0, height: 20, fontSize: 11 }}
        />
      </Box>

      <Typography
        fontSize={12.5}
        color="text.secondary"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {item.description || "설명 없음"}
      </Typography>

      {hasOverkill && (
        <Box
          sx={{
            fontSize: 11.5,
            px: 0.75,
            py: 0.1,
            width: "fit-content",
            borderRadius: 1,
            bgcolor: "#fff3e0",
            color: "#ef6c00",
          }}
        >
          과검률 {overkillPercent}%
        </Box>
      )}
    </ListItem>
  );
};
